import { Button, ConfigProvider, DatePicker, Form, Input, InputNumber, message, Modal, Select, Switch } from "antd";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { ArticleRepo } from "@/core/repos/tables/article.repo";
import { CategoryArticleRepo } from "@/core/repos/tables/category-article.repo";
import { ArticleModel, ArticleModelFactory } from "@/core/models/article.model";
import { CategoryArticleModel } from "@/core/models/category-article.model";
import { useDrafts } from "@/core/stores/drafts.store";
import { ResetDraftButton } from "@/app/home/components/drafts/ResetDraftButton";
import { UploadForm, UploadFormHandle } from "@/app/home/components/upload/UploadForm";
import { toSlug, shouldAutoGenerateSlug } from "@/core/utils/slug.util";
import { DEF_DATETIME_FORMAT } from "@/app/home/utils/global.utils";
import { ArticleRichTextEditor } from "../components/ArticleRichTextEditor";
import { ArticleCoverUpload } from "../components/ArticleCoverUpload";

const articleRepo = container.get(ArticleRepo);
const categoryArticleRepo = container.get(CategoryArticleRepo);

interface DetailModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    data: ArticleModel;
}

const INPUT_WIDTH = 880;

export const DetailModal: React.FC<DetailModalProps> = ({
    isModalOpen,
    setIsModalOpen,
    data,
}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<ArticleModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [draftArticleId, setDraftArticleId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [categories, setCategories] = useState<CategoryArticleModel[]>([]);
    const [urlNameTouched, setUrlNameTouched] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const uploadFormRef = useRef<UploadFormHandle>(null);
    const isAutoUpdatingSlug = useRef(false);
    const watchedName = Form.useWatch("name", form);

    const isExistingArticle = Boolean(data.id);
    const autoSlugEnabled = shouldAutoGenerateSlug(isExistingArticle, urlNameTouched);

    const articleId = data.id || draftArticleId || undefined;
    const formDisabled = isEdit && !permissionStore.page.edit;

    const draft = useDrafts((state) => state.getDraft("ArticleModel"));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft("ArticleModel", form.getFieldsValue());
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft("ArticleModel");
        form.setFieldsValue(data);
    };

    const loadCategories = async () => {
        const list = await categoryArticleRepo.getAll();
        setCategories(list);
    };

    const resetModalState = () => {
        setDraftArticleId(null);
        setUrlNameTouched(false);
    };

    const markSlugManualIfNeeded = (name?: string, urlName?: string) => {
        if (urlName && urlName !== toSlug(name ?? "")) {
            setUrlNameTouched(true);
        }
    };

    const handleDataChange = async () => {
        resetModalState();
        if (data.id && data.id.length > 0) {
            setLoading(true);
            const detail = await articleRepo.detail(data.id);
            const article = detail ?? data;
            form.setFieldsValue({
                ...article,
                publishDate: dayjsPipe(
                    typeof article.publishDate === "string"
                        ? article.publishDate
                        : undefined
                ),
            });
            setIsEdit(true);
            setLoading(false);
        } else {
            if (draft) {
                form.setFieldsValue({
                    ...draft,
                    publishDate: dayjsPipe(draft.publishDate as string),
                });
                markSlugManualIfNeeded(draft.name, draft.urlName);
            } else {
                form.setFieldsValue({
                    ...ArticleModelFactory.getEmpty(),
                    ...data,
                    publishDate: dayjsPipe(data.publishDate as string),
                });
            }
            setIsEdit(false);
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            handleDataChange();
        } else {
            resetModalState();
        }
    }, [data, isModalOpen]);

    useEffect(() => {
        if (isModalOpen) {
            loadCategories();
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (!isModalOpen || !autoSlugEnabled) return;
        isAutoUpdatingSlug.current = true;
        form.setFieldValue("urlName", toSlug(watchedName ?? ""));
        queueMicrotask(() => {
            isAutoUpdatingSlug.current = false;
        });
    }, [watchedName, autoSlugEnabled, isModalOpen, form]);

    const resyncSlugFromTitle = () => {
        const title = form.getFieldValue("name") ?? "";
        setUrlNameTouched(false);
        isAutoUpdatingSlug.current = true;
        form.setFieldValue("urlName", toSlug(title));
        queueMicrotask(() => {
            isAutoUpdatingSlug.current = false;
        });
    };

    const handleSlugChange = () => {
        if (!isAutoUpdatingSlug.current) {
            setUrlNameTouched(true);
        }
    };

    const buildPayload = (formData: ArticleModel): Partial<ArticleModel> => ({
        name: formData.name,
        summary: formData.summary ?? "",
        contents: formData.contents ?? "",
        image: formData.image ?? "",
        catId: formData.catId || undefined,
        urlName: formData.urlName,
        langCode: formData.langCode ?? "vi",
        pubYN: formData.pubYN ?? false,
        isHot: formData.isHot ?? false,
        publishDate: formData.publishDate,
        priority: formData.priority ?? 0,
        metaTitle: formData.metaTitle ?? "",
        metaDescription: formData.metaDescription ?? "",
        video: formData.video ?? "",
        tags: formData.tags ?? "",
    });

    const ensureArticleId = async (): Promise<string | null> => {
        if (data.id) return data.id;
        if (draftArticleId) return draftArticleId;

        try {
            await form.validateFields(["name", "urlName"]);
        } catch {
            messageApi.warning(
                "Vui lòng nhập tiêu đề và đường dẫn trước khi upload ảnh"
            );
            return null;
        }

        const formData = form.getFieldsValue();
        const payload = buildPayload(formData);
        const result = await articleRepo.create({
            ...payload,
            id: "",
            pubYN: false,
        } as ArticleModel);

        if (result?.id) {
            setDraftArticleId(result.id);
            setIsEdit(true);
            messageApi.info("Đã tạo bản nháp để upload ảnh");
            return result.id;
        }

        messageApi.error("Không thể tạo bản nháp bản tin");
        return null;
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            const payload = buildPayload(formData);
            const targetId = data.id || draftArticleId;

            if (targetId) {
                const result = await articleRepo.update({ ...payload, id: targetId });
                if (result) {
                    await handleUploadAttachments(targetId);
                    messageApi.success({ content: "Đã cập nhật bản tin" });
                    setIsModalOpen(false);
                } else {
                    messageApi.error({ content: "Cập nhật bản tin thất bại" });
                }
            } else {
                const result = await articleRepo.create({
                    ...payload,
                    id: "",
                } as ArticleModel);
                clearDraft();
                if (result?.id) {
                    await handleUploadAttachments(result.id);
                    messageApi.success({ content: "Thêm bản tin mới thành công" });
                    setIsModalOpen(false);
                } else {
                    handleUploadReset();
                    messageApi.error({ content: "Thêm bản tin mới thất bại" });
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const handleCancel = () => {
        if (!isEdit && !draftArticleId) saveDraft();
        setIsModalOpen(false);
    };

    const handleUploadAttachments = async (referenceId: string) => {
        if (uploadFormRef.current) {
            await uploadFormRef.current.saveUploadedFiles(referenceId);
        }
    };

    const handleUploadReset = () => {
        if (uploadFormRef.current) uploadFormRef.current.reset();
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal
                open={isModalOpen}
                onCancel={handleCancel}
                width={960}
                centered
                destroyOnClose
                styles={{
                    mask: {
                        backdropFilter: "blur(2px)",
                        backgroundColor: "var(--primary-mask)",
                    },
                    body: { maxHeight: "75vh", overflowY: "auto" },
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton
                            isEdit={isEdit || !!draftArticleId}
                            loading={loading}
                            onClick={handleSave}
                        />
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">
                        {isEdit || draftArticleId
                            ? "CHỈNH SỬA BẢN TIN"
                            : "THÊM BẢN TIN"}
                    </p>
                    {!isEdit && !draftArticleId && (
                        <ResetDraftButton onClick={clearDraft} />
                    )}
                </div>
                <div className="flex flex-row justify-center mt-6 px-1">
                    <Form<ArticleModel>
                        name="articleForm"
                        className="w-full"
                        form={form}
                        layout="vertical"
                        disabled={formDisabled}
                    >
                        <div className="grid grid-cols-1 gap-x-4">
                            <Form.Item<ArticleModel>
                                label="Tiêu đề"
                                name="name"
                                rules={[
                                    {
                                        required: true,
                                        message: "Trường này là bắt buộc!",
                                    },
                                ]}
                            >
                                <Input placeholder="Nhập tiêu đề bản tin" />
                            </Form.Item>

                            <div className="grid grid-cols-2 gap-x-4">
                                <Form.Item<ArticleModel>
                                    label={
                                        <span>
                                            Đường dẫn (slug)
                                            {autoSlugEnabled && (
                                                <span className="ml-2 text-xs font-normal text-gray-400">
                                                    Tự động từ tiêu đề
                                                </span>
                                            )}
                                        </span>
                                    }
                                    name="urlName"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Trường này là bắt buộc!",
                                        },
                                    ]}
                                    extra={
                                        !isExistingArticle && urlNameTouched ? (
                                            <Button
                                                type="link"
                                                size="small"
                                                className="px-0"
                                                onClick={resyncSlugFromTitle}
                                            >
                                                Sinh lại từ tiêu đề
                                            </Button>
                                        ) : undefined
                                    }
                                >
                                    <Input
                                        placeholder="thong-bao-trien-khai-he-thong"
                                        onChange={handleSlugChange}
                                    />
                                </Form.Item>

                                <Form.Item<ArticleModel> label="Danh mục" name="catId">
                                    <Select
                                        allowClear
                                        placeholder="Chọn danh mục"
                                        options={categories.map((cat) => ({
                                            label: cat.name,
                                            value: cat.id,
                                        }))}
                                    />
                                </Form.Item>
                            </div>

                            <Form.Item<ArticleModel> label="Tóm tắt" name="summary">
                                <Input.TextArea
                                    placeholder="Nhập mô tả ngắn hiển thị trên danh sách tin"
                                    rows={2}
                                    style={{ resize: "none" }}
                                />
                            </Form.Item>

                            <ArticleCoverUpload
                                articleId={articleId}
                                onEnsureArticleId={ensureArticleId}
                                disabled={formDisabled}
                            />

                            <Form.Item<ArticleModel>
                                label="Nội dung"
                                name="contents"
                                rules={[
                                    {
                                        required: true,
                                        message: "Vui lòng nhập nội dung bản tin",
                                    },
                                    {
                                        validator: (_, val) => {
                                            const text = (val ?? "")
                                                .replace(/<[^>]+>/g, "")
                                                .replace(/&nbsp;/g, " ")
                                                .trim();
                                            if (!text) {
                                                return Promise.reject(
                                                    new Error(
                                                        "Vui lòng nhập nội dung bản tin"
                                                    )
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <ArticleRichTextEditor
                                    key={articleId ?? "new-article"}
                                    onEnsureArticleId={ensureArticleId}
                                    disabled={formDisabled}
                                />
                            </Form.Item>

                            <div className="grid grid-cols-2 gap-x-4">
                                <Form.Item<ArticleModel>
                                    label="Ngày xuất bản"
                                    name="publishDate"
                                >
                                    <DatePicker
                                        showTime
                                        format={DEF_DATETIME_FORMAT}
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>

                                <Form.Item<ArticleModel> label="Thứ tự" name="priority">
                                    <InputNumber
                                        min={0}
                                        placeholder="0"
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </div>

                            <div className="grid grid-cols-2 gap-x-4">
                                <Form.Item<ArticleModel>
                                    label="Xuất bản"
                                    name="pubYN"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item<ArticleModel>
                                    label="Tin nổi bật"
                                    name="isHot"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </div>

                            <div className="grid grid-cols-2 gap-x-4">
                                <Form.Item<ArticleModel> label="Meta title" name="metaTitle">
                                    <Input placeholder="Tiêu đề SEO" />
                                </Form.Item>

                                <Form.Item<ArticleModel>
                                    label="Meta description"
                                    name="metaDescription"
                                >
                                    <Input placeholder="Mô tả SEO" />
                                </Form.Item>
                            </div>

                            <UploadForm
                                ref={uploadFormRef}
                                objectType="Article"
                                referenceId={articleId ?? ""}
                                text="File đính kèm khác (PDF, tài liệu...)"
                            />
                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    );
};
